import React, { useState, useEffect } from "react";
import AnimatedModal from "./AnimatedModal";
import { api } from "../utils/api";
import jwtUtils from "../utils/jwtUtils";

export default function EmployeesModal({ isOpen, onClose }) {
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isAdmin] = useState(jwtUtils.isAdmin());
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Load employees when modal opens
  useEffect(() => {
    if (isOpen && isAdmin) {
      loadEmployees();
    }
  }, [isOpen, isAdmin]);

  const loadEmployees = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await api.get('/api/users');
      console.log('Employees loaded:', response);
      setEmployees(response.users || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
      setError("Failed to load employees. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };



  const handleStatusChange = async (userId, newStatus) => {
    try {
      setError("");
      const isActive = newStatus === 'active';
      await api.put(`/api/users/${userId}/status`, { isActive });
      
      // Find the employee to get their name for the success message
      const employee = employees.find(emp => emp.id === userId);
      const userName = employee ? 
        (employee.firstName && employee.lastName ? `${employee.firstName} ${employee.lastName}` : employee.firstName || employee.email || 'User') : 
        'User';
      
      setSuccessMessage(`Status updated successfully for ${userName}`);
      
      // Update the local state
      setEmployees(prev => prev.map(emp => 
        emp.id === userId ? { ...emp, status: newStatus } : emp
      ));
      
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error('Failed to update status:', error);
      setError("Failed to update user status. Please try again.");
    }
  };

  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setShowDetailsModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
      case 'super_admin':
        return 'badge-danger';
      case 'user':
        return 'badge-info';
      default:
        return 'badge-secondary';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'badge-success';
      case 'suspended':
        return 'badge-danger';
      default:
        return 'badge-success';
    }
  };

  // Filter and sort employees
  const filteredAndSortedEmployees = employees
    .filter(employee => {
      const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.toLowerCase();
      const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                           employee.email?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          bValue = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          break;
        case 'status':
          aValue = a.status || 'active';
          bValue = b.status || 'active';
          break;
        case 'loginTime':
          aValue = new Date(a.loginTime || 0);
          bValue = new Date(b.loginTime || 0);
          break;
        case 'chatCount':
          aValue = a.sessions?.length || 0;
          bValue = b.sessions?.length || 0;
          break;
        default:
          aValue = a.firstName || '';
          bValue = b.firstName || '';
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  if (!isAdmin) {
    return (
      <AnimatedModal isOpen={isOpen} onClose={onClose} className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Only administrators can access employee management.
          </p>
          <button
            onClick={onClose}
                            className="btn-secondary-sm"
          >
            Close
          </button>
        </div>
      </AnimatedModal>
    );
  }

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} className="w-full max-w-6xl max-h-[90vh]">
      <div className="bg-gradient-to-br from-white via-gray-50/50 to-indigo-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm relative overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Enhanced Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-indigo-500/10 via-purple-500/5 to-transparent rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-blue-500/8 via-teal-500/4 to-transparent rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2 animate-pulse" style={{animationDuration: '6s'}}></div>
        </div>

        {/* Enhanced Header with Gradient */}
        <div className="relative z-10 flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                    </svg>
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-900 animate-pulse"></div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Employee Management
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Manage your team members and monitor their activity
                  </p>
                </div>
              </div>
              <button 
                onClick={onClose} 
                className="btn-icon"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Enhanced Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search employees by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200/60 dark:border-gray-700/60 rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white dark:focus:bg-gray-800 transition-all shadow-sm hover:shadow-md"
                />
              </div>
                <div className="flex gap-3">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="input-primary"
                >
                  <option value="name">Sort by Name</option>
                  <option value="status">Sort by Status</option>
                  <option value="loginTime">Sort by Login Time</option>
                  <option value="chatCount">Sort by Chat Count</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="btn-icon"
                  title={sortOrder === 'asc' ? 'Sort Descending' : 'Sort Ascending'}
                >
                  <svg className={`w-5 h-5 transition-transform duration-300 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                </button>
              </div>
              <button
                onClick={loadEmployees}
                disabled={isLoading}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 disabled:hover:scale-100"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="relative z-10 flex-1 overflow-y-auto px-8 pb-8">
          {/* Enhanced Error/Success Messages */}
          {error && (
            <div className="mb-6 p-4 card-primary">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
              </div>
            </div>
          )}
          {successMessage && (
            <div className="mb-6 p-4 card-primary">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-emerald-700 dark:text-emerald-300 font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Enhanced Employees List */}
          <div className="card-primary overflow-hidden">
            {isLoading ? (
              <div className="p-12 text-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mx-auto mb-6"></div>
                <div className="space-y-2">
                  <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">Loading employees...</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Fetching the latest data from server</p>
                </div>
              </div>
            ) : filteredAndSortedEmployees.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl flex items-center justify-center shadow-lg">
                  <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  No employees found
                </h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                  {searchTerm 
                    ? "No employees match your search criteria. Try adjusting your filters or search terms."
                    : "No employees have been registered yet. New employees will appear here once they sign up."
                  }
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-blue-50/50 dark:from-gray-700/80 dark:to-gray-600/50 backdrop-blur-sm">
                    <tr>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                          Employee
                        </div>
                      </th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 6V5a6 6 0 1112 0v1h2a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h2zm2-1a4 4 0 118 0v1H8V5zm1 5a1 1 0 012 0v1a1 1 0 01-2 0v-1z" clipRule="evenodd" />
                          </svg>
                          Role
                        </div>
                      </th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Status
                        </div>
                      </th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          Last Login
                        </div>
                      </th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          Sessions
                        </div>
                      </th>
                      <th className="px-6 py-5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 8a2 2 0 110-4 2 2 0 010 4zM10 18a8 8 0 100-16 8 8 0 000 16zM8 9a1 1 0 000 2h4a1 1 0 100-2H8z" />
                          </svg>
                          Actions
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200/60 dark:divide-gray-700/60">{filteredAndSortedEmployees.map((employee) => (
                      <tr key={employee.id} className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/30 dark:hover:from-blue-900/20 dark:hover:to-indigo-900/10 transition-all duration-200 group">
                        <td className="px-6 py-5 whitespace-nowrap">
                          <div className="flex items-center gap-4">
                            <div className="relative">
                              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-600 to-blue-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg group-hover:scale-105 transition-transform">
                                {employee.firstName?.charAt(0)?.toUpperCase() || employee.email?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-800"></div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                {employee.firstName && employee.lastName ? `${employee.firstName} ${employee.lastName}` : employee.firstName || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                {employee.email}
                              </div>
                              {employee.companyName && (
                                <div className="text-xs text-gray-500 dark:text-gray-500 truncate mt-1">
                                  <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm3 1h6v4H7V5z" clipRule="evenodd" />
                                  </svg>
                                  {employee.companyName}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <span className={`px-3 py-1.5 text-xs font-semibold rounded-full shadow-sm ${getRoleBadgeColor(employee.role)}`}>
                            {employee.role || 'user'}
                          </span>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <select
                            value={employee.status || 'active'}
                            onChange={(e) => handleStatusChange(employee.id, e.target.value)}
                            className="px-3 py-2 text-xs font-medium rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all shadow-sm hover:shadow-md"
                          >
                            <option value="active" className="text-gray-900 dark:text-gray-100">Active</option>
                            <option value="suspended" className="text-gray-900 dark:text-gray-100">Suspended</option>
                          </select>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                            </svg>
                            {formatDate(employee.loginTime)}
                          </div>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 dark:from-blue-900/40 dark:to-indigo-900/40 dark:text-blue-300 shadow-sm">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                            </svg>
                            {employee.sessions?.length || 0}
                          </span>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(employee)}
                            className="inline-flex items-center px-4 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-white hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-600 rounded-xl transition-all duration-200 border border-indigo-200 dark:border-indigo-700 hover:border-transparent shadow-sm hover:shadow-lg hover:scale-105"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}</tbody>
                </table>
              </div>
            )}
          </div>

          {/* Enhanced Summary */}
          <div className="mt-8 p-6 bg-gradient-to-r from-indigo-50/80 via-purple-50/60 to-blue-50/80 dark:from-indigo-900/30 dark:via-purple-900/20 dark:to-blue-900/30 rounded-2xl border border-indigo-200/50 dark:border-indigo-700/50 backdrop-blur-sm">
            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
              Summary Statistics
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white/60 dark:bg-gray-800/40 rounded-xl shadow-sm">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{employees.length}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Total Users</div>
              </div>
              <div className="text-center p-4 bg-white/60 dark:bg-gray-800/40 rounded-xl shadow-sm">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{filteredAndSortedEmployees.length}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Showing</div>
              </div>
              <div className="text-center p-4 bg-white/60 dark:bg-gray-800/40 rounded-xl shadow-sm">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{employees.filter(e => e.role === 'admin' || e.role === 'super_admin').length}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Admins</div>
              </div>
              <div className="text-center p-4 bg-white/60 dark:bg-gray-800/40 rounded-xl shadow-sm">
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{employees.reduce((total, emp) => total + (emp.sessions?.length || 0), 0)}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Total Sessions</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Employee Details Modal */}
      {showDetailsModal && selectedEmployee && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md">
          <div className="card-primary rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 via-purple-600 to-blue-600 rounded-3xl flex items-center justify-center text-white font-bold text-2xl shadow-xl">
                      {selectedEmployee.firstName?.charAt(0)?.toUpperCase() || selectedEmployee.email?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full border-3 border-white dark:border-gray-900 shadow-lg"></div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {selectedEmployee.firstName && selectedEmployee.lastName ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : selectedEmployee.firstName || 'N/A'}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">{selectedEmployee.email}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(selectedEmployee.role)}`}>
                        {selectedEmployee.role || 'user'}
                      </span>
                      {selectedEmployee.companyName && (
                        <span className="px-3 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                          {selectedEmployee.companyName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="btn-icon"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto modal-scrollable max-h-[calc(90vh-200px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Enhanced User Information */}
                <div className="space-y-6">
                  <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                    User Information
                  </h4>
                  <div className="card-primary p-6 space-y-4">
                    <div className="flex justify-between items-center p-3 bg-white/60 dark:bg-gray-800/40 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-400 font-medium flex items-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                        Full Name:
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {selectedEmployee.firstName && selectedEmployee.lastName ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white/60 dark:bg-gray-800/40 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-400 font-medium flex items-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                        Email:
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{selectedEmployee.email}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white/60 dark:bg-gray-800/40 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-400 font-medium flex items-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm3 1h6v4H7V5z" clipRule="evenodd" />
                        </svg>
                        Company:
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{selectedEmployee.companyName || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white/60 dark:bg-gray-800/40 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-400 font-medium flex items-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 6V5a6 6 0 1112 0v1h2a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h2zm2-1a4 4 0 118 0v1H8V5zm1 5a1 1 0 012 0v1a1 1 0 01-2 0v-1z" clipRule="evenodd" />
                        </svg>
                        Role:
                      </span>
                      <span className={`px-3 py-1.5 text-xs font-semibold rounded-full ${getRoleBadgeColor(selectedEmployee.role)}`}>
                        {selectedEmployee.role || 'user'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white/60 dark:bg-gray-800/40 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-400 font-medium flex items-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        Last Login:
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{formatDate(selectedEmployee.loginTime)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white/60 dark:bg-gray-800/40 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-400 font-medium flex items-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                        User ID:
                      </span>
                      <span className="font-mono text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{selectedEmployee.id}</span>
                    </div>
                  </div>
                </div>

                {/* Enhanced Chat Sessions */}
                <div className="space-y-6">
                  <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    Chat Sessions ({selectedEmployee.sessions?.length || 0})
                  </h4>
                  <div className="card-primary p-6 max-h-80 overflow-y-auto modal-scrollable">
                    {selectedEmployee.sessions && selectedEmployee.sessions.length > 0 ? (
                      <div className="space-y-4">
                        {selectedEmployee.sessions.map((session, index) => (
                          <div key={session.id} className="border border-gray-200/60 dark:border-gray-700/60 rounded-xl p-4 bg-white/60 dark:bg-gray-800/40 backdrop-blur-sm hover:shadow-md transition-all">
                            <div className="flex justify-between items-start mb-3">
                              <h5 className="font-semibold text-gray-900 dark:text-gray-100 text-sm flex items-center gap-2">
                                <svg className="w-4 h-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                                </svg>
                                {session.title || `Session ${index + 1}`}
                              </h5>
                              <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                                {formatDate(session.createdAt)}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                                </svg>
                                Messages: <span className="font-semibold text-gray-900 dark:text-gray-100">{session.messages?.length || 0}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                </svg>
                                Updated: <span className="font-semibold text-gray-900 dark:text-gray-100">{formatDate(session.updatedAt)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <h5 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">No chat sessions</h5>
                        <p className="text-sm">This user hasn't started any conversations yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AnimatedModal>
  );
} 